import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";

type BusinessInfo = {
  ho_kd_name: string;
  address: string;
  tax_code: string;
  location: string;
  representative_name: string;
  currency: string;
  updated_at?: string;
};

const emptyForm = (): BusinessInfo => ({
  ho_kd_name: "",
  address: "",
  tax_code: "",
  location: "",
  representative_name: "",
  currency: "đồng",
});

export function BusinessInfoPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const qc = useQueryClient();
  const [form, setForm] = useState<BusinessInfo>(emptyForm());
  const [saved, setSaved] = useState(false);

  const q = useQuery({
    queryKey: ["business-info"],
    queryFn: async () => (await api.get<BusinessInfo>("/business-info")).data,
  });
  useEffect(() => {
    if (q.data) setForm(q.data);
  }, [q.data]);

  const save = useMutation({
    mutationFn: async () => (await api.put<BusinessInfo>("/business-info", form)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["business-info"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-xl font-semibold text-slate-900 mb-1">Thông tin hộ kinh doanh</h1>
      <p className="text-sm text-slate-500 mb-5">Dùng để in header sổ S2a-HKD và các báo cáo kế toán.</p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (isAdmin) save.mutate();
        }}
        className="bg-white border rounded p-4 space-y-3"
      >
        <Field
          label="Tên hộ / cá nhân kinh doanh *"
          required
          value={form.ho_kd_name}
          disabled={!isAdmin}
          onChange={(v) => setForm({ ...form, ho_kd_name: v })}
        />
        <Field
          label="Mã số thuế *"
          required
          value={form.tax_code}
          disabled={!isAdmin}
          onChange={(v) => setForm({ ...form, tax_code: v })}
        />
        <Field
          label="Người đại diện"
          value={form.representative_name}
          disabled={!isAdmin}
          onChange={(v) => setForm({ ...form, representative_name: v })}
        />
        <Field
          label="Địa chỉ"
          textarea
          value={form.address}
          disabled={!isAdmin}
          onChange={(v) => setForm({ ...form, address: v })}
        />
        <Field
          label="Địa điểm kinh doanh (in trên sổ)"
          value={form.location}
          disabled={!isAdmin}
          onChange={(v) => setForm({ ...form, location: v })}
        />
        <Field
          label="Đơn vị tiền tệ"
          value={form.currency}
          disabled={!isAdmin}
          onChange={(v) => setForm({ ...form, currency: v })}
        />

        {!isAdmin && (
          <div className="text-sm text-gray-500 italic">Chỉ admin được sửa.</div>
        )}
        {save.error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
            {(save.error as any)?.response?.data?.detail ?? String(save.error)}
          </div>
        )}
        {saved && (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded px-3 py-2 text-sm">
            ✓ Đã lưu.
          </div>
        )}
        {isAdmin && (
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={save.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-60"
            >
              {save.isPending ? "Đang lưu..." : "Lưu"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  textarea,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  textarea?: boolean;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-0.5">{label}</label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          disabled={disabled}
          className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-gray-50"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          disabled={disabled}
          className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-gray-50"
        />
      )}
    </div>
  );
}
